import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { authService } from "@/api/services/authService";
import { RootState } from "../reducer/reducer";

interface Branch {
  branchId: string;
  branchName: string;
  branchCode:string;
}

interface BranchState {
  selectedBranch: Branch | null;
  branches: Branch[];
  loading: boolean;
  error: string | null;
}

const storedBranch = localStorage.getItem("selectedBranch");

const initialState: BranchState = {
  selectedBranch: storedBranch ? JSON.parse(storedBranch) : null,
  branches: [],
  loading: false,
  error: null,
};

// Async thunk to fetch branches
export const fetchBranches = createAsyncThunk(
  "branch/fetchBranches",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const branchIds = state.auth.branches; // Fetching branch IDs from authSlice

      if (!branchIds.length) return rejectWithValue("No branches available");

      const response = await authService.branchById({ branchIds });

      if (!response || !Array.isArray(response)) {
        return rejectWithValue("Invalid response format");
      }

      return response as Branch[]; // Ensure response matches expected structure
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : "An unexpected error occurred");
    }
  }
);

const branchSlice = createSlice({
  name: "branch",
  initialState,
  reducers: {
    setSelectedBranch: (state, action: PayloadAction<Branch>) => {
      state.selectedBranch = action.payload;
      localStorage.setItem("selectedBranch", JSON.stringify(action.payload));
    },
    clearSelectedBranch: (state) => {
      state.selectedBranch = null;
      localStorage.removeItem("selectedBranch");
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBranches.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBranches.fulfilled, (state, action: PayloadAction<Branch[]>) => {
        state.loading = false;
        state.branches = action.payload;
      })
      .addCase(fetchBranches.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setSelectedBranch, clearSelectedBranch } = branchSlice.actions;
export default branchSlice.reducer;
